import type { App } from "../types/platform";

export const apps: readonly App[] = [
  {
    name: "ILS",
    tagline: "Intelligent Learning System.",
    url: "https://ils.opensesame.art",
    category: "customer",
    status: "in-dev",
    agentPartner: "Plan",
    secondaryAgent: "Verify",
    why:
      "Customers buy training and then have to run it themselves. Someone assigns it, chases it, and reports on it, for every worker, all year. That work never gets done well by hand. ILS is where OpenSesame does it for them.",
    description:
      "Offers, delivers, and tracks training for each worker. Three layers: mandated by the company, directed by role and risk, discovered by the worker.",
    how:
      "Plan decides what each worker needs from role, prior performance, format preference, and time available. Conduit supplies roles. Atlas and Marketplace supply the courses. Skyfall delivers and reports back. Behavioral telemetry lands in the data lake.",
    now: [
      "Onboarding and role management",
      "Three-layer delivery model",
      "Authorization and telemetry foundation"
    ],
    next: [
      "Five-signal recommendation engine",
      "Feast feature store",
      "Sam and Dave prototype demo",
      "Slack, SMS, and email surfaces",
      "Continuous competency verification"
    ]
  },
  {
    name: "Marketplace",
    tagline: "Catalog storefront.",
    url: "https://marketplace.opensesame.art",
    category: "customer",
    status: "running",
    agentPartner: "Plan",
    why:
      "Buyers need one place to find the right course from many publishers without vetting each one. Publishers need one place to reach customers without building a sales channel.",
    description:
      "The storefront for 60,000+ courses from 200+ publishers. Search, curated collections, trial and preview, purchase.",
    how:
      "Atlas powers search and recommendation. Enigma supplies the structure behind each course. Plan uses both to source off-the-shelf training for ILS.",
    now: [
      "60,000+ courses, 200+ publishers",
      "Curated collections and lists",
      "Trial and preview workflows"
    ],
    next: [
      "AI-curated discovery",
      "Buyer success signals",
      "Integrated skill paths"
    ]
  },
  {
    name: "Coach",
    tagline: "Voice-based skill assessment.",
    url: "https://coach.opensesame.art",
    category: "customer",
    status: "demoable",
    agentPartner: "Verify",
    why:
      "Completion is not capability. A finished course shows someone sat through it. Companies need to know the person can do the job, and they need it checked again as the job changes.",
    description:
      "An AI interviewer that assesses a worker's skill in conversation. Three modes: Skill Assessment (scored), Skill Check (short), and Skill Practice (unscored).",
    how:
      "Voice pipeline on Pipecat with Deepgram, ElevenLabs, and Claude. Scoring against role and skill rubrics using the OpenSesame Skill Assessment Standard. Rubrics tag to O*NET, ESCO, and customer taxonomies. Cost target under one dollar per session.",
    now: [
      "Voice-driven competency interviews",
      "Role-rubric alignment scoring",
      "Assessment methodology and standard"
    ],
    next: [
      "Customer-authored assessments and coaches",
      "Continuous re-verification cadence",
      "Signal back into the learner record",
      "Multiple languages"
    ]
  },
  {
    name: "Simon",
    tagline: "AI authoring.",
    url: "https://simon.opensesame.art",
    category: "customer",
    status: "in-dev",
    agentPartner: "Build",
    why:
      "When the right course doesn't exist, custom content takes months and an outside vendor. By then the need has moved. Companies need training built on their own procedures at the speed the business changes.",
    description:
      "Turns a storyboard into a finished course: script, casting, visuals, narration, assembly, with a human review gate at each stage.",
    how:
      "Build runs eight specialized agents from instructional design through production. Restate for durable workflows, Langfuse for observability, LLM Guard for safety. Translation is a separate post-production pipeline.",
    now: [
      "ISD storyboard to production pipeline",
      "Specialized agents per production stage",
      "Storyboard review with the first outside tester"
    ],
    next: [
      "Storyboard to polished production",
      "Casting virtual actors and voices",
      "Self-serve authoring experience",
      "Short-form adaptive content",
      "Multilingual production at scale"
    ]
  },
  {
    name: "ECP",
    tagline: "Commercial engine.",
    url: "https://ecp.opensesame.art",
    category: "customer",
    status: "running",
    agentPartner: "Deliver",
    secondaryAgent: "Watch",
    why:
      "Every course play has to be licensed, counted, and paid for. Customers need to know what they own. Publishers need to trust the royalty number. Neither works if the counting is manual.",
    description:
      "Licensing, entitlement, royalty tracking for publishers, and revenue attribution across the Marketplace.",
    how:
      "Skyfall enforces entitlement at delivery and reports every play. Deliver is the primary agent. Watch tracks health and cost.",
    now: [
      "Licensing and entitlement",
      "Royalty usage tracking for publishers",
      "Marketplace revenue attribution"
    ],
    next: [
      "Usage-based pricing tiers",
      "Consumption analytics",
      "Self-serve publisher portal"
    ]
  },
  {
    name: "Proof",
    tagline: "Verified credential wallet.",
    url: "https://proof.opensesame.art",
    category: "b2proc",
    status: "in-dev",
    agentPartner: "Prove",
    why:
      "A worker's proven skills are scattered across systems and lost at every job change. The record should belong to both the worker and the company, and the worker should carry it forward. The first Innovation Lab project.",
    description:
      "A mobile wallet for verified credentials, skills, and employment history. Shared by the worker and the employer. Portable across employers and systems.",
    how:
      "W3C Verifiable Credentials 2.0, Open Badges 3.0, and CLR 2.0. Passkey-first with wallet encryption derived from the passkey. Native app on React Native. Employment data through Conduit.",
    now: [
      "Wallet core, built in vertical slices on device",
      "Nursing credentials as the first wedge",
      "Employer roles: inviter, attestor, sponsor, verifier"
    ],
    next: [
      "Cross-employer credential portability",
      "Employment verification, one entry at a time",
      "Shipping this year"
    ]
  },
  {
    name: "CSR",
    tagline: "Customer service workspace.",
    url: "https://csr.opensesame.art",
    category: "internal",
    status: "running",
    agentPartner: "Watch",
    why:
      "Staff support customers across many services. Without one workspace they work from a dozen tabs, and a customer's full picture lives in nobody's head.",
    description:
      "One place for customer and tenant management, support, and cross-service actions.",
    how:
      "Watch supplies health and cost. Conduit supplies the employment data behind each tenant. Agent actions are logged for audit.",
    now: [
      "Customer and tenant management",
      "Cross-service orchestration",
      "Ops and support tooling"
    ],
    next: [
      "Unified admin experience",
      "Agent action audit log",
      "Tenant health dashboard"
    ]
  },
  {
    name: "Ops",
    tagline: "Operations dashboard.",
    url: "https://ops.opensesame.art",
    category: "internal",
    status: "running",
    agentPartner: "Watch",
    why:
      "A system that runs itself has to watch itself. Agents cost money on every call and can fail quietly. Staff need to see health and cost before a customer feels it.",
    description:
      "Service health, cost per workflow, and incident response across every cell.",
    how:
      "Langfuse for AI observability, telemetry from Skyfall and Conduit, per-workflow cost accounting.",
    now: [
      "Service health monitoring",
      "Cost tracking per workflow",
      "Incident response tooling"
    ],
    next: [
      "Predictive cost alerts",
      "Auto-remediation flows",
      "Cross-cell observability"
    ]
  }
] as const;
